/**
 * Logique pure du bloc « statut du dernier calcul » du détail d'une tournée : message indexé par
 * le statut du dernier run (`draft`, `running`, `infeasible`, `incomplete`, `failed`…), et lignes de
 * diagnostic lisibles du dernier run. Aucun JSX, testée par `run-status.test.mjs`.
 *
 * Le statut opérationnel peut rester `validated` pendant qu'un nouveau calcul échoue. Le bloc
 * doit donc distinguer ce statut de celui du dernier run.
 */

import type {
  RunDiagnostic,
  SimulationDetailLatestRun,
  SimulatorScreenStatus,
} from '~/lib/logistics-simulations-query'
import { type ScenarioPlace, vehicleFallbackLabel } from './scenario-view'

export interface RunStatusInput {
  diagnostics: RunDiagnostic[]
  error: string | null
}

const RUN_STATUS_MESSAGE: Partial<Record<SimulatorScreenStatus, string>> = {
  draft: 'Aucun calcul n’a encore été lancé pour cette tournée.',
  running: 'Calcul en cours…',
  infeasible: 'Aucune combinaison de véhicules et de chauffeurs ne permet de livrer ce périmètre.',
  incomplete: 'Des données requises manquent encore pour lancer un calcul complet.',
  failed: 'Le dernier calcul a échoué.',
}

/** Statuts d'écran pour lesquels le dernier calcul n'a rien produit d'exploitable. */
const PROBLEM_STATUSES: ReadonlySet<SimulatorScreenStatus> = new Set([
  'infeasible',
  'incomplete',
  'failed',
])

export const isRunProblemStatus = (status: SimulatorScreenStatus | undefined): boolean =>
  status !== undefined && PROBLEM_STATUSES.has(status)

/**
 * Incomplet parce que les horaires se contredisent (`schedule_inconsistent`, #903), sans paramètre
 * manquant : le diagnostic dit quelle heure changer. `buildProblem` peut signaler les deux à la
 * fois ; le titre des paramètres manquants l'emporte alors, les horaires restent dans les lignes.
 */
const SCHEDULE_INCONSISTENT_MESSAGE =
  'Les horaires de la tournée se contredisent : le calcul n’a pas pu démarrer.'

/**
 * Incomplet parce qu'aucun camion ou aucun chauffeur ne convient (#936) : le simulateur n'en a
 * trouvé aucun, ou le camion imposé d'une tournée antérieure n'est plus mobilisable. Lu par code,
 * jamais par texte : celui du diagnostic, en ligne dessous, dit pourquoi (aucun rattaché, plus
 * actif, sans contenant…).
 */
const NO_RESOURCE_MESSAGES = {
  vehicle: 'Aucun camion ne convient à cette tournée : le calcul n’a pas pu démarrer.',
  driver: 'Aucun chauffeur ne convient à cette tournée : le calcul n’a pas pu démarrer.',
  both: 'Aucun camion ni chauffeur ne convient à cette tournée : le calcul n’a pas pu démarrer.',
} as const

/** Infaisable à cause d'un créneau de réception de boutique : la flotte n'y est pour rien. */
const STORE_WINDOW_INFEASIBLE_MESSAGE =
  'Le créneau de réception d’une boutique rend cette tournée impossible, quels que soient les véhicules et les chauffeurs.'

/**
 * Message du bloc de statut, indexé par le statut d'écran. Un calcul infaisable à cause d'un
 * créneau de réception (`infeasibleCause`, dérivée par l'API) ne met pas en cause la flotte ; un
 * calcul incomplet parce que les horaires se contredisent, ou faute de camion ou de chauffeur, ne
 * dit pas qu'un paramètre manque. Un paramètre manquant garde le titre, puis les horaires : les
 * autres causes restent dans les lignes.
 */
export const runStatusMessageOf = (
  status: SimulatorScreenStatus | undefined,
  latestRun: Pick<SimulationDetailLatestRun, 'infeasibleCause' | 'diagnostics'> | null,
): string => {
  if (status === 'infeasible' && latestRun?.infeasibleCause === 'store_window') {
    return STORE_WINDOW_INFEASIBLE_MESSAGE
  }
  if (status === 'incomplete' && latestRun !== null) {
    const codes = new Set(latestRun.diagnostics.map((diagnostic) => diagnostic.code))
    if (!codes.has('parameter_missing')) {
      if (codes.has('schedule_inconsistent')) return SCHEDULE_INCONSISTENT_MESSAGE
      const noVehicle = codes.has('no_vehicle_available')
      const noDriver = codes.has('no_driver_available')
      if (noVehicle && noDriver) return NO_RESOURCE_MESSAGES.both
      if (noVehicle) return NO_RESOURCE_MESSAGES.vehicle
      if (noDriver) return NO_RESOURCE_MESSAGES.driver
    }
  }
  return (
    (status === undefined ? undefined : RUN_STATUS_MESSAGE[status]) ??
    'Aucun scénario actif pour cette tournée.'
  )
}

/**
 * Libellé du sujet d'un diagnostic : lieux résolus par `placeLabel` (atelier, hub, boutique du
 * détail), autres sujets nommés par leur type.
 */
export const diagnosticSubjectLabel = (
  subject: NonNullable<RunDiagnostic['subject']>,
  placeLabel: (place: ScenarioPlace) => string,
): string => {
  switch (subject.type) {
    case 'central_kitchen':
    case 'hub':
    case 'store':
    case 'store_project': {
      return placeLabel({ type: subject.type, id: Number(subject.id) })
    }
    case 'vehicle': {
      return vehicleFallbackLabel(subject.id)
    }
    case 'driver': {
      return `Chauffeur n° ${subject.id}`
    }
    default: {
      return `Paramètre « ${subject.id} »`
    }
  }
}

/**
 * Lignes à afficher sous le message pour un calcul `infeasible`, `incomplete` ou `failed` :
 * l'erreur du run en tête s'il a échoué, puis chaque diagnostic (message, et sujet si présent).
 * Lignes identiques dédoublonnées. Aucune ligne pour les autres statuts.
 */
export const runDiagnosticLinesOf = (
  status: SimulatorScreenStatus | undefined,
  latestRun: RunStatusInput | null | undefined,
  placeLabel: (place: ScenarioPlace) => string,
): string[] => {
  if (!isRunProblemStatus(status) || latestRun === null || latestRun === undefined) return []

  const lines: string[] = []
  if (status === 'failed' && latestRun.error !== null && latestRun.error !== '') {
    lines.push(latestRun.error)
  }
  for (const diagnostic of latestRun.diagnostics) {
    lines.push(
      diagnostic.subject === undefined
        ? diagnostic.message
        : `${diagnosticSubjectLabel(diagnostic.subject, placeLabel)} : ${diagnostic.message}`,
    )
  }
  // Un même manque peut être signalé par plusieurs familles de scénarios : une ligne suffit.
  return [...new Set(lines)]
}

/** Relecture d'un run pendant son calcul (`GET /logistics/runs/:id`), état React Query réduit. */
export interface RunFollowUpQuery {
  status: 'pending' | 'error' | 'success'
  data?: { displayStatus: SimulatorScreenStatus }
}

/**
 * Relire le run tant que l'API l'affiche en cours : c'est elle qui décide d'un abandon (plafond de
 * durée et marge, sur son horloge), le navigateur ne compare jamais la sienne à `startedAt`. Une
 * relecture en échec (après les tentatives de React Query) arrête le suivi.
 */
export const shouldPollRun = (query: RunFollowUpQuery): boolean =>
  query.status !== 'error' && (query.data === undefined || query.data.displayStatus === 'running')

/**
 * Suivi terminé pour un détail qui affiche encore le calcul en cours : le run est conclu (ou
 * abandonné), ou sa relecture a échoué. Le détail et la liste sont alors relus une fois.
 */
export const isRunFollowUpSettled = (detailRunning: boolean, query: RunFollowUpQuery): boolean =>
  detailRunning && !shouldPollRun(query)
